"use client";

import * as React from "react";

import { cn } from "@/app/_lib/utils";

/**
 * Greyscale serif "Selected clients" auto-moving slider. Reuses the existing
 * `.ticker-row` keyframe (globals.css). Items are tripled for a seamless loop
 * (the keyframe translates -33.333%). On hover, the running CSS animation's
 * playback rate is scaled down via the Web Animations API instead of pausing
 * it — this decelerates in place (no restart/jump) and speeds back up from
 * wherever it left off on mouse leave.
 */
export interface ClientTickerProps {
  items: React.ReactNode[];
  className?: string;
}

export function ClientTicker({ items, className }: ClientTickerProps) {
  const row = [...items, ...items, ...items];
  const rowRef = React.useRef<HTMLDivElement>(null);

  const setPlaybackRate = (rate: number) => {
    const el = rowRef.current;
    if (!el) return;
    for (const anim of el.getAnimations()) {
      anim.playbackRate = rate;
    }
  };

  return (
    <div
      className={cn(
        "relative overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_10%,black_90%,transparent)]",
        className,
      )}
    >
      <div
        ref={rowRef}
        onMouseEnter={() => setPlaybackRate(0.3)}
        onMouseLeave={() => setPlaybackRate(1)}
        className="ticker-row flex w-max items-center gap-16 md:gap-24"
      >
        {/* ── LOGO IMPLEMENTATION (greyscale, un-greys on hover) ────────────
            Each item is expected to be a client logo node (<img>/<LogoImage>).
            The wrapper enforces a consistent height and the greyscale look. */}
        {row.map((item, i) => (
          <div
            key={i}
            className="flex shrink-0 items-center justify-center opacity-70 grayscale transition duration-300 hover:opacity-100 hover:grayscale-0 [&_img]:h-8 [&_img]:w-auto [&_img]:object-contain md:[&_img]:h-10"
          >
            {item}
          </div>
        ))}

        {/* ── TEXT IMPLEMENTATION (client names) — commented out for now ────
        {row.map((item, i) => (
          <span
            key={i}
            className="whitespace-nowrap font-serif text-2xl hover:text-primary text-muted-foreground"
          >
            {item}
          </span>
        ))}
        */}
      </div>
    </div>
  );
}
